
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Copy, Share, RotateCcw, MessageSquare, Edit3, Bookmark, Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { TopicType } from '@/types/assistant';

interface ContextualQuickActionsProps {
  messageId: string;
  messageContent: string;
  topic: TopicType;
  onRegenerateMessage?: (messageId: string) => void;
  onEditMessage?: (messageId: string) => void;
}

export const ContextualQuickActions: React.FC<ContextualQuickActionsProps> = ({
  messageId,
  messageContent,
  topic,
  onRegenerateMessage,
  onEditMessage
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const { toast } = useToast();

  const getTopicColor = () => {
    switch (topic) {
      case 'guide': return 'text-botscheme-royal-purple';
      case 'performance': return 'text-botscheme-ocean-blue';
      case 'trading_strategy': return 'text-botscheme-emerald-green';
      case 'advisor': return 'text-botscheme-amber-gold';
      default: return 'text-primary';
    }
  };

  const topicColor = getTopicColor();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageContent);
      toast({
        description: "Message copied to clipboard",
        duration: 2000,
      });
    } catch (error) {
      toast({
        description: "Failed to copy message",
        variant: "destructive",
        duration: 2000,
      });
    }
    setIsExpanded(false);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'AI Assistant Message',
          text: messageContent,
        });
      } catch (error) {
        handleCopy();
      }
    } else {
      handleCopy();
    }
    setIsExpanded(false);
  };

  const handleRegenerate = () => {
    if (onRegenerateMessage) {
      onRegenerateMessage(messageId);
      toast({
        description: "Regenerating message...",
        duration: 2000,
      });
    }
    setIsExpanded(false);
  };

  const handleEdit = () => {
    if (onEditMessage) {
      onEditMessage(messageId);
    }
    setIsExpanded(false);
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    toast({
      description: isBookmarked ? "Bookmark removed" : "Message bookmarked",
      duration: 2000,
    });
    setIsExpanded(false);
  };

  const handleReport = () => {
    toast({
      description: "Message reported for review",
      duration: 2000,
    });
    setIsExpanded(false);
  };

  const quickActions = [
    {
      icon: Copy,
      label: "Copy",
      action: handleCopy,
      color: "text-blue-500"
    },
    {
      icon: Share,
      label: "Share",
      action: handleShare,
      color: "text-green-500"
    },
    {
      icon: RotateCcw,
      label: "Regenerate",
      action: handleRegenerate,
      color: "text-orange-500",
      show: !!onRegenerateMessage
    },
    {
      icon: Edit3,
      label: "Edit",
      action: handleEdit,
      color: "text-purple-500",
      show: !!onEditMessage
    },
    {
      icon: Bookmark,
      label: isBookmarked ? "Remove bookmark" : "Bookmark",
      action: handleBookmark,
      color: isBookmarked ? "text-yellow-500" : "text-gray-500"
    },
    {
      icon: Flag,
      label: "Report",
      action: handleReport,
      color: "text-red-500"
    }
  ].filter(action => action.show !== false);

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <motion.div
          animate={{ rotate: isExpanded ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <MoreHorizontal className="h-4 w-4" />
        </motion.div>
      </Button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 z-50"
          >
            <div className="bg-background/95 backdrop-blur-md border border-white/20 rounded-xl shadow-xl p-2 min-w-48">
              <div className="grid grid-cols-2 gap-1">
                {quickActions.map((action, index) => {
                  const IconComponent = action.icon;
                  return (
                    <motion.button
                      key={action.label}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 text-sm rounded-lg",
                        "hover:bg-white/10 transition-colors duration-200",
                        "text-left w-full"
                      )}
                      onClick={action.action}
                    >
                      <IconComponent className={cn("h-4 w-4", action.color)} />
                      <span className="truncate">{action.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};
